// Authentication service using Firebase Auth
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { auth, db } from "../firebase";
import { assertValidOrganizationDomain, normalizeDomain } from "../utils/domainUtils";

// Initialize providers
const googleProvider = new GoogleAuthProvider();

const microsoftProvider = new OAuthProvider("microsoft.com");
microsoftProvider.setCustomParameters({ prompt: "select_account" }); // Let user choose a different account each time

/**
 * Sign in with Google
 * @returns {Promise<UserCredential>}
 */
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result;
  } catch (error) {
    console.error("Error signing in with Google:", error);
    throw error;
  }
};

/**
 * Sign in with Microsoft
 * @returns {Promise<UserCredential>}
 */
export const signInWithMicrosoft = async () => {
  try {
    const result = await signInWithPopup(auth, microsoftProvider);
    return result;
  } catch (error) {
    console.error("Error signing in with Microsoft:", error);
    throw error;
  }
};

/**
 * Sign out current user
 * @returns {Promise<void>}
 */
export const signOut = async () => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error("Error signing out:", error);
    throw error;
  }
};

/**
 * Check if email is from syllysync.com domain
 * @param {string} email
 * @returns {boolean}
 */
export const isSyllySyncDomain = (email) => {
  if (!email) return false;
  const lowerEmail = email.toLowerCase();
  return (
    lowerEmail.endsWith("@admin.syllysync.com") ||
    lowerEmail.endsWith("@syllysync.com")
  );
};

/**
 * Check if email is from .edu domain or allowed dev domain
 * @param {string} email
 * @returns {boolean}
 */
export const isEduDomain = (email) => {
  if (!email) return false;
  const lowerEmail = email.toLowerCase();
  return (
    lowerEmail.endsWith(".edu") ||
    lowerEmail.endsWith("@syllysyncdev.com")
  );
};

export const isSyllySyncDevDomain = (email) =>
  !!email && email.toLowerCase().endsWith("@syllysyncdev.com");

/**
 * Extract domain from email
 * @param {string} email
 * @returns {string|null}
 */
export const getEmailDomain = (email) => {
  if (!email) return null;
  const parts = email.toLowerCase().split("@");
  return parts.length === 2 ? parts[1] : null;
};

/**
 * Check if user has courses or assignments (indicating they're a student)
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export const isStudent = async (userId) => {
  try {
    // Check if user has any courses
    const coursesRef = collection(db, "users", userId, "courses");
    const coursesSnapshot = await getDocs(query(coursesRef, limit(1)));

    if (!coursesSnapshot.empty) {
      return true; // Has courses, likely a student
    }

    // If no courses, check if any assignments exist in any course
    // We'll check the first course's assignments if courses exist
    // But since we already checked courses, if we get here, there are no courses
    // So we can safely return false
    return false;
  } catch (error) {
    console.error("Error checking if user is student:", error);
    return false; // Default to not a student if we can't determine
  }
};

/**
 * Get organization by id (domain or composite id like example.edu--slug)
 * @param {string} orgId - Document id (e.g. "example.edu" or "example.edu--chess-club")
 * @returns {Promise<object|null>}
 */
export const getOrganization = async (orgId) => {
  try {
    const orgRef = doc(db, "organizations", orgId);
    const orgDoc = await getDoc(orgRef);

    if (orgDoc.exists()) {
      return { id: orgDoc.id, ...orgDoc.data() };
    }
    return null;
  } catch (error) {
    console.error("Error getting organization:", error);
    return null;
  }
};

/**
 * Get university organization for an email domain (exact match; used to check if user can create on-campus org)
 * @param {string} domain - Email domain (e.g. "example.edu")
 * @returns {Promise<object|null>}
 */
export const getUniversityForDomain = async (domain) => {
  const org = await getOrganization(domain);
  if (!org || org.orgType !== "university") return null;
  // Consider active if they have billing or are set up (optional: check stripeCustomerId or fundedByUniversity)
  return org;
};

/**
 * Get all organizations the user can access (admin or team member)
 * @param {string} userId
 * @returns {Promise<Array<{ id: string, name: string, orgType: string, domain?: string }>>}
 */
export const getAccessibleOrganizations = async (userId) => {
  try {
    const orgsRef = collection(db, "organizations");
    const [adminSnap, teamSnap] = await Promise.all([
      getDocs(query(orgsRef, where("admins", "array-contains", userId))),
      getDocs(query(orgsRef, where("teamMembers", "array-contains", userId))),
    ]);
    const byId = new Map();
    const add = (docSnap) => {
      if (!docSnap.exists()) return;
      const d = docSnap.data();
      const id = docSnap.id;
      if (!byId.has(id)) {
        byId.set(id, {
          id,
          name: d.name || id,
          orgType: d.orgType || "university",
          domain: d.domain || id,
          fundedByUniversity: !!d.fundedByUniversity,
        });
      }
    };
    adminSnap.docs.forEach(add);
    teamSnap.docs.forEach(add);
    return Array.from(byId.values());
  } catch (error) {
    console.error("Error getting accessible organizations:", error);
    return [];
  }
};

/**
 * Create a slug from org name for use in composite org id
 * @param {string} name
 * @returns {string}
 */
function slugFromOrgName(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    || "org";
}

/**
 * Create a university organization automatically (no approval needed)
 * @param {string} userId
 * @param {string} email
 * @param {string} domain - Email domain (e.g. "example.edu")
 * @param {string} orgName - Display name (e.g. "University of Pennsylvania")
 * @param {string} requestedByName - The name of person requesting
 * @returns {Promise<{ orgId: string }>}
 */
export const createUniversityOrganization = async (
  userId,
  email,
  domain,
  orgName,
  requestedByName
) => {
  domain = assertValidOrganizationDomain(domain);

  // Check if university already exists for this domain
  const existing = await getOrganization(domain);
  if (existing && existing.orgType === "university") {
    throw new Error("A university organization already exists for this domain.");
  }

  const orgRef = doc(db, "organizations", domain);
  const orgData = {
    domain,
    name: orgName,
    orgType: "university",
    adminId: userId,
    admins: [userId],
    teamMembers: [],
    members: [],
    createdAt: new Date(),
  };
  if (isSyllySyncDevDomain(email) && domain === "syllysyncdev.com") {
    orgData.billingExempt = true;
    orgData.billingExemptReason = "internal_development";
  }
  await setDoc(orgRef, orgData);
  return { orgId: domain };
};

/**
 * Create an on-campus organization under an existing active university (no request/approval).
 * User becomes admin; org is active immediately and has no billing (funded by university).
 * @param {string} userId
 * @param {string} email
 * @param {string} domain - Email domain (e.g. "example.edu")
 * @param {string} orgName - Display name (e.g. "Chess Club")
 * @param {string} requestedByName
 * @returns {Promise<{ orgId: string }>}
 */
export const createOnCampusOrgUnderUniversity = async (
  userId,
  email,
  domain,
  orgName,
  requestedByName
) => {
  domain = assertValidOrganizationDomain(domain);

  const university = await getUniversityForDomain(domain);
  if (!university) {
    throw new Error("No active university found for this email domain.");
  }
  const baseSlug = slugFromOrgName(orgName);
  let slug = baseSlug;
  let attempt = 0;
  let orgId = `${domain}--${slug}`;
  while (true) {
    const existing = await getOrganization(orgId);
    if (!existing) break;
    attempt += 1;
    slug = `${baseSlug}-${attempt}`;
    orgId = `${domain}--${slug}`;
  }
  const orgRef = doc(db, "organizations", orgId);
  const orgData = {
    domain,
    universityDomain: domain,
    name: orgName,
    orgType: "on_campus_org",
    fundedByUniversity: true,
    adminId: userId,
    admins: [userId],
    teamMembers: [],
    members: [],
    allowedEmails: [],
    createdAt: new Date(),
  };
  await setDoc(orgRef, orgData);
  return { orgId };
};

/**
 * Check if user is admin of an organization
 * @param {string} userId
 * @param {string} domain
 * @returns {Promise<boolean>}
 */
export const isOrgAdmin = async (userId, domain) => {
  try {
    const org = await getOrganization(domain);
    if (!org) return false;

    return (
      org.adminId === userId || (org.admins && org.admins.includes(userId))
    );
  } catch (error) {
    console.error("Error checking org admin:", error);
    return false;
  }
};

/**
 * Check if user is a team member (not admin) of an organization
 * @param {string} userId
 * @param {string} orgId
 * @returns {Promise<boolean>}
 */
export const isOrgTeamMember = async (userId, orgId) => {
  try {
    const org = await getOrganization(orgId);
    if (!org) return false;
    return !!(org.teamMembers && org.teamMembers.includes(userId));
  } catch (error) {
    console.error("Error checking org team member:", error);
    return false;
  }
};

/**
 * Create an organization request
 * @param {string} userId
 * @param {string} email
 * @param {string} domain
 * @param {string} orgName - The name of the organization
 * @param {string} requestedByName - The name of the person requesting
 * @param {string} orgType - 'university' or 'on_campus_org'
 * @returns {Promise<void>}
 */
export const createOrgRequest = async (userId, email, domain, orgName, requestedByName, orgType = 'university') => {
  try {
    domain = assertValidOrganizationDomain(domain);

    const requestRef = doc(collection(db, "orgRequests"));
    await setDoc(requestRef, {
      userId,
      email,
      domain,
      orgName,
      requestedByName,
      name: requestedByName, // Keep for backward compatibility
      orgType,
      status: "pending",
      createdAt: new Date(),
      requestedAt: new Date(),
    });
  } catch (error) {
    console.error("Error creating org request:", error);
    throw error;
  }
};

/**
 * Approve an organization request
 * If approving an on-campus org and a university already exists for that domain, use composite id (domain--slug).
 * @param {string} requestId
 * @param {object} requestData
 * @returns {Promise<void>}
 */
export const approveOrgRequest = async (requestId, requestData) => {
  try {
    const approvedDomain = assertValidOrganizationDomain(requestData.domain);
    requestData = {
      ...requestData,
      domain: approvedDomain,
    };

    const requestRef = doc(db, "orgRequests", requestId);
    await setDoc(
      requestRef,
      {
        ...requestData,
        status: "approved",
        approvedAt: new Date(),
      },
      { merge: true }
    );

    let orgId = requestData.domain;
    const isOnCampus = requestData.orgType === 'on_campus_org';
    const university = await getUniversityForDomain(requestData.domain);

    if (isOnCampus && university) {
      const baseSlug = slugFromOrgName(requestData.orgName || requestData.name || 'org');
      let slug = baseSlug;
      let attempt = 0;
      orgId = `${requestData.domain}--${slug}`;
      while (await getOrganization(orgId)) {
        attempt += 1;
        slug = `${baseSlug}-${attempt}`;
        orgId = `${requestData.domain}--${slug}`;
      }
    }

    const orgRef = doc(db, "organizations", orgId);
    const orgData = {
      domain: requestData.domain,
      adminId: requestData.userId,
      admins: [requestData.userId],
      name: requestData.orgName || requestData.name,
      orgType: requestData.orgType || 'university',
      createdAt: new Date(),
    };
    if (isOnCampus && university) {
      orgData.universityDomain = requestData.domain;
      orgData.fundedByUniversity = true;
    }
    if (isOnCampus) {
      orgData.allowedEmails = [];
      orgData.members = [];
      orgData.teamMembers = [];
    } else {
      orgData.members = [];
      orgData.teamMembers = [];
    }
    await setDoc(orgRef, orgData);
  } catch (error) {
    console.error("Error approving org request:", error);
    throw error;
  }
};

/**
 * Reject an organization request
 * @param {string} requestId
 * @param {object} requestData
 * @returns {Promise<void>}
 */
export const rejectOrgRequest = async (requestId, requestData) => {
  try {
    const requestRef = doc(db, "orgRequests", requestId);
    await setDoc(
      requestRef,
      {
        ...requestData,
        status: "rejected",
        rejectedAt: new Date(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error rejecting org request:", error);
    throw error;
  }
};

/**
 * Create a member request for an existing organization
 * @param {string} userId
 * @param {string} email
 * @param {string} domain
 * @param {string} name
 * @returns {Promise<void>}
 */
export const createMemberRequest = async (userId, email, domain, name) => {
  try {
    domain = normalizeDomain(domain);

    const requestRef = doc(collection(db, "orgMemberRequests"));
    await setDoc(requestRef, {
      userId,
      email,
      domain,
      name,
      status: "pending",
      createdAt: new Date(),
      requestedAt: new Date(),
    });
  } catch (error) {
    console.error("Error creating member request:", error);
    throw error;
  }
};

/**
 * Check if user has a pending member request
 * @param {string} userId
 * @param {string} domain
 * @returns {Promise<boolean>}
 */
export const hasPendingMemberRequest = async (userId, domain) => {
  try {
    const requestsRef = collection(db, "orgMemberRequests");
    const q = query(
      requestsRef,
      where("userId", "==", userId),
      where("domain", "==", domain),
      where("status", "==", "pending")
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error("Error checking member request:", error);
    return false;
  }
};

/**
 * Check if user has an approved member request (is an approved member)
 * @param {string} userId
 * @param {string} domain
 * @returns {Promise<boolean>}
 */
export const hasApprovedMemberRequest = async (userId, domain) => {
  try {
    const requestsRef = collection(db, "orgMemberRequests");
    const q = query(
      requestsRef,
      where("userId", "==", userId),
      where("domain", "==", domain),
      where("status", "==", "approved")
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error("Error checking approved member request:", error);
    return false;
  }
};

/**
 * Approve a member request
 * This adds the user to members array and allowlist (for on-campus orgs)
 * Does NOT add to teamMembers - that must be done separately via promotion
 * @param {string} requestId
 * @param {object} requestData
 * @returns {Promise<void>}
 */
export const approveMemberRequest = async (requestId, requestData) => {
  try {
    // Update request status to approved
    const requestRef = doc(db, "orgMemberRequests", requestId);
    await setDoc(
      requestRef,
      {
        ...requestData,
        status: "approved",
        approvedAt: new Date(),
      },
      { merge: true }
    );

    const orgRef = doc(db, "organizations", requestData.domain);
    const orgDoc = await getDoc(orgRef);

    if (orgDoc.exists()) {
      const orgData = orgDoc.data();
      const members = orgData.members || [];
      const allowedEmails = orgData.allowedEmails || [];
      const isOnCampusOrg = orgData.orgType === 'on_campus_org';

      // For on-campus orgs, add email to allowlist
      // The Cloud Function will handle adding to members array when allowlist is updated
      if (isOnCampusOrg && requestData.email) {
        const emailLower = requestData.email.toLowerCase();
        if (!allowedEmails.some(e => e.toLowerCase() === emailLower)) {
          allowedEmails.push(requestData.email);
        }
      }

      // For on-campus orgs: Only add to allowlist, let Cloud Function handle members array
      // The Cloud Function will add existing users with org subscriptions to members when allowlist is updated
      // For university orgs: Add to members array directly
      if (!isOnCampusOrg) {
        // University orgs: Add to members array if not already there
        if (!members.includes(requestData.userId)) {
          members.push(requestData.userId);
        }
      } else {
        // On-campus orgs: Check if user already exists and has org subscription
        // If they do, DON'T add to members - let the Cloud Function handle it when allowlist updates
        // Only add to members if user doesn't exist yet (so they're included when they sign up)
        const userRef = doc(db, "users", requestData.userId);
        const userDoc = await getDoc(userRef);
        
        let shouldAddToMembers = false;
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const subscriptionSource = userData.subscriptionSource;
          const organizationDomains = userData.organizationDomains || [];
          const organizationDomain = userData.organizationDomain;
          
          // User exists - check if they have org subscription for this domain
          const hasOrgSubscription = subscriptionSource === 'organization';
          const hasThisDomain = organizationDomains.includes(requestData.domain) || organizationDomain === requestData.domain;
          
          // If user already has org subscription for this domain, DON'T add to members
          // The Cloud Function will handle it when allowlist is updated
          // Only add if they don't have org subscription yet (they'll get it when they sign up)
          if (!hasOrgSubscription || !hasThisDomain) {
            // User doesn't have org subscription for this domain yet - add to members
            // The Cloud Function will grant them org subscription when allowlist updates
            shouldAddToMembers = true;
          }
          // If they already have org subscription for this domain, let Cloud Function handle it completely
        } else {
          // User doesn't exist yet - add to members so they're included when they sign up
          shouldAddToMembers = true;
        }

        // Add to members array if needed and not already there
        if (shouldAddToMembers && !members.includes(requestData.userId)) {
          members.push(requestData.userId);
        }
      }

      await setDoc(
        orgRef,
        {
          ...orgData,
          members,
          allowedEmails,
        },
        { merge: true }
      );
    }
  } catch (error) {
    console.error("Error approving member request:", error);
    throw error;
  }
};

/**
 * Reject a member request
 * @param {string} requestId
 * @param {object} requestData
 * @returns {Promise<void>}
 */
export const rejectMemberRequest = async (requestId, requestData) => {
  try {
    const requestRef = doc(db, "orgMemberRequests", requestId);
    await setDoc(
      requestRef,
      {
        ...requestData,
        status: "rejected",
        rejectedAt: new Date(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error rejecting member request:", error);
    throw error;
  }
};

/**
 * Get all team members for an organization
 * Only returns users who are in teamMembers or admins arrays (not regular members)
 * @param {string} domain
 * @returns {Promise<Array>} Array of {userId, email, name, isAdmin}
 */
export const getOrgTeamMembers = async (domain) => {
  try {
    // Get organization to find admins and team members
    const org = await getOrganization(domain);
    if (!org) {
      return [];
    }

    const adminIds = org.admins || [];
    if (org.adminId && !adminIds.includes(org.adminId)) {
      adminIds.push(org.adminId);
    }

    const teamMemberIds = org.teamMembers || [];
    
    // Combine admins and team members (admins are also team members)
    const allTeamMemberIds = new Set([...adminIds, ...teamMemberIds]);
    
    const teamMembers = [];

    // Fetch user data for each team member/admin
    for (const userId of allTeamMemberIds) {
      try {
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const isAdmin = userId === org.adminId || adminIds.includes(userId);
          
          teamMembers.push({
            userId,
            email: userData.email || "",
            name: userData.displayName || userData.name || userData.email?.split("@")[0] || "Unknown",
            photoURL: userData.photoURL || null,
            isAdmin,
          });
        }
      } catch (error) {
        console.error(`Error getting user doc for ${userId}:`, error);
      }
    }

    // Sort: admins first, then by name
    teamMembers.sort((a, b) => {
      if (a.isAdmin !== b.isAdmin) {
        return b.isAdmin ? 1 : -1;
      }
      return a.name.localeCompare(b.name);
    });

    return teamMembers;
  } catch (error) {
    console.error("Error getting org team members:", error);
    throw error;
  }
};

/**
 * Promote a member to team member
 * Adds user to teamMembers array (gives admin panel access but not admin privileges)
 * @param {string} userId
 * @param {string} domain
 * @returns {Promise<void>}
 */
export const promoteMemberToTeamMember = async (userId, domain) => {
  try {
    const orgRef = doc(db, "organizations", domain);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      throw new Error("Organization not found");
    }

    const orgData = orgDoc.data();
    const teamMembers = orgData.teamMembers || [];

    // Add to teamMembers array if not already there
    if (!teamMembers.includes(userId)) {
      teamMembers.push(userId);
    }

    await setDoc(
      orgRef,
      {
        ...orgData,
        teamMembers,
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error promoting member to team member:", error);
    throw error;
  }
};

/**
 * Demote a team member back to regular member
 * Removes user from teamMembers array
 * @param {string} userId
 * @param {string} domain
 * @returns {Promise<void>}
 */
export const demoteTeamMemberToMember = async (userId, domain) => {
  try {
    const orgRef = doc(db, "organizations", domain);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      throw new Error("Organization not found");
    }

    const orgData = orgDoc.data();
    const teamMembers = (orgData.teamMembers || []).filter(id => id !== userId);

    await setDoc(
      orgRef,
      {
        ...orgData,
        teamMembers,
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error demoting team member:", error);
    throw error;
  }
};

/**
 * Upgrade a member to admin
 * Adds user to admins array (gives full admin privileges including billing access)
 * @param {string} userId
 * @param {string} domain
 * @returns {Promise<void>}
 */
export const upgradeMemberToAdmin = async (userId, domain) => {
  try {
    const orgRef = doc(db, "organizations", domain);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      throw new Error("Organization not found");
    }

    const orgData = orgDoc.data();
    const admins = orgData.admins || [];

    // Add to admins array if not already there
    if (!admins.includes(userId)) {
      admins.push(userId);
    }

    // Also ensure they're in teamMembers if not already (admins should have team member access too)
    const teamMembers = orgData.teamMembers || [];
    if (!teamMembers.includes(userId)) {
      teamMembers.push(userId);
    }

    await setDoc(
      orgRef,
      {
        ...orgData,
        admins,
        teamMembers,
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error upgrading member to admin:", error);
    throw error;
  }
};

/**
 * Demote an admin to team member or regular member
 * Removes user from admins array
 * @param {string} userId
 * @param {string} domain
 * @param {boolean} keepAsTeamMember - If true, keeps them as team member; if false, removes from teamMembers too
 * @returns {Promise<void>}
 */
export const demoteAdmin = async (userId, domain, keepAsTeamMember = true) => {
  try {
    const orgRef = doc(db, "organizations", domain);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      throw new Error("Organization not found");
    }

    const orgData = orgDoc.data();
    const admins = (orgData.admins || []).filter(id => id !== userId);
    let teamMembers = orgData.teamMembers || [];

    if (!keepAsTeamMember) {
      // Remove from teamMembers as well
      teamMembers = teamMembers.filter(id => id !== userId);
    } else if (!teamMembers.includes(userId)) {
      // Keep as team member if not already there
      teamMembers.push(userId);
    }

    await setDoc(
      orgRef,
      {
        ...orgData,
        admins,
        teamMembers,
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error demoting admin:", error);
    throw error;
  }
};

/**
 * Add email to organization allowlist (for on-campus orgs)
 * @param {string} email
 * @param {string} domain
 * @returns {Promise<void>}
 */
export const addEmailToAllowlist = async (email, domain) => {
  try {
    const orgRef = doc(db, "organizations", domain);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      throw new Error("Organization not found");
    }

    const orgData = orgDoc.data();
    const allowedEmails = orgData.allowedEmails || [];

    if (!allowedEmails.includes(email)) {
      allowedEmails.push(email);
      await setDoc(
        orgRef,
        {
          ...orgData,
          allowedEmails,
        },
        { merge: true }
      );
    }
  } catch (error) {
    console.error("Error adding email to allowlist:", error);
    throw error;
  }
};

/**
 * Remove email from organization allowlist
 * @param {string} email
 * @param {string} domain
 * @returns {Promise<void>}
 */
export const removeEmailFromAllowlist = async (email, domain) => {
  try {
    const orgRef = doc(db, "organizations", domain);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      throw new Error("Organization not found");
    }

    const orgData = orgDoc.data();
    const allowedEmails = (orgData.allowedEmails || []).filter(e => e !== email);

    await setDoc(
      orgRef,
      {
        ...orgData,
        allowedEmails,
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error removing email from allowlist:", error);
    throw error;
  }
};

/**
 * Get organization members (students with org subscriptions)
 * Only returns users who are in the org's members array (and for on-campus, in allowedEmails).
 * Does not query by organizationDomain so that deleted-and-recreated orgs don't show stale members.
 * Includes admin status for each member.
 * @param {string} domain
 * @returns {Promise<Array>} Array of {userId, email, name, isAdmin, isTeamMember}
 */
export const getOrgMembers = async (domain) => {
  try {
    const org = await getOrganization(domain);
    if (!org) {
      return [];
    }

    const memberIds = org.members || [];
    const adminIds = org.admins || [];
    const teamMemberIds = org.teamMembers || [];
    const adminId = org.adminId;
    const members = [];
    const isOnCampusOrg = org.orgType === 'on_campus_org';

    for (const userId of memberIds) {
      try {
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // For on-campus orgs, verify email is in allowedEmails
          if (isOnCampusOrg) {
            const allowedEmails = org.allowedEmails || [];
            const userEmail = (userData.email || "").toLowerCase();
            const isAllowed = allowedEmails.some(
              (allowedEmail) => allowedEmail.toLowerCase() === userEmail
            );
            if (!isAllowed) {
              continue;
            }
          }

          const isAdmin = userId === adminId || adminIds.includes(userId);
          const isTeamMember = teamMemberIds.includes(userId);
          members.push({
            userId,
            email: userData.email || "",
            name: userData.displayName || userData.name || userData.email?.split("@")[0] || "Unknown",
            photoURL: userData.photoURL || null,
            isAdmin,
            isTeamMember,
          });
        }
      } catch (error) {
        console.error(`Error getting user doc for ${userId}:`, error);
      }
    }

    members.sort((a, b) => a.name.localeCompare(b.name));
    return members;
  } catch (error) {
    console.error("Error getting org members:", error);
    throw error;
  }
};

/**
 * Add a user to organization members array
 * This allows students to be part of multiple organizations
 * Also updates the user's organizationDomains array
 * @param {string} userId
 * @param {string} domain
 * @returns {Promise<void>}
 */
export const addUserToOrgMembers = async (userId, domain) => {
  try {
    const addMember = httpsCallable(functions, "addOrganizationMember");
    await addMember({ orgId: domain, userId });
  } catch (error) {
    console.error("Error adding user to org members:", error);
    throw error;
  }
};

export const addUserToOrgMembersByEmail = async (email, domain) => {
  try {
    const addMember = httpsCallable(functions, "addOrganizationMember");
    await addMember({ orgId: domain, email });
  } catch (error) {
    console.error("Error adding user to org members by email:", error);
    throw error;
  }
};

export const searchOrganizationUsers = async (domain, term) => {
  const searchUsers = httpsCallable(functions, "searchOrganizationUsers");
  const result = await searchUsers({ orgId: domain, term });
  return result.data.users || [];
};

/**
 * Remove a user from organization members array
 * Also updates the user's organizationDomains array
 * @param {string} userId
 * @param {string} domain
 * @returns {Promise<void>}
 */
export const removeUserFromOrgMembers = async (userId, domain) => {
  try {
    const orgRef = doc(db, "organizations", domain);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      throw new Error("Organization not found");
    }

    const orgData = orgDoc.data();
    const members = (orgData.members || []).filter(id => id !== userId);

    await setDoc(
      orgRef,
      {
        ...orgData,
        members,
      },
      { merge: true }
    );

    // Also update user's organizationDomains array to remove this org
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const currentDomains = userData.organizationDomains || [];
      const updatedDomains = currentDomains.filter(d => d !== domain);
      
      await setDoc(
        userRef,
        {
          ...userData,
          organizationDomains: updatedDomains,
          // If this was their primary org and they have other orgs, update primary
          ...(userData.organizationDomain === domain && updatedDomains.length > 0 ? {
            organizationDomain: updatedDomains[0], // Set first remaining org as primary
          } : {}),
          // If no orgs left, remove org subscription (but keep other data)
          ...(updatedDomains.length === 0 && userData.subscriptionSource === 'organization' ? {
            subscriptionStatus: null,
            subscriptionSource: null,
            organizationDomain: null,
          } : {}),
        },
        { merge: true }
      );
    }
  } catch (error) {
    console.error("Error removing user from org members:", error);
    throw error;
  }
};

/**
 * Get organization team members (admin access but not admins)
 * @param {string} domain
 * @returns {Promise<Array>} Array of {userId, email, name}
 */
export const getOrgTeamMembersList = async (domain) => {
  try {
    const org = await getOrganization(domain);
    if (!org) {
      return [];
    }

    const teamMemberIds = org.teamMembers || [];
    const teamMembers = [];

    // Fetch user data for each team member
    for (const userId of teamMemberIds) {
      try {
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          teamMembers.push({
            userId,
            email: userData.email || "",
            name: userData.displayName || userData.name || userData.email?.split("@")[0] || "Unknown",
            photoURL: userData.photoURL || null,
          });
        }
      } catch (error) {
        console.error(`Error getting user doc for ${userId}:`, error);
      }
    }

    return teamMembers;
  } catch (error) {
    console.error("Error getting org team members:", error);
    throw error;
  }
};

/**
 * Remove a member from organization
 * This will:
 * 1. Remove user from organization admins array
 * 2. Delete user's Firestore document
 * 3. Delete all orgMemberRequests for this user/domain
 * 4. Delete user's Firebase Auth account
 * @param {string} userId
 * @param {string} domain
 * @returns {Promise<void>}
 */
export const removeOrgMember = async (userId, domain) => {
  try {
    const orgRef = doc(db, "organizations", domain);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      throw new Error("Organization not found");
    }

    const orgData = orgDoc.data();
    const admins = orgData.admins || [];

    // Include adminId in the admins list if not already there
    const allAdmins = [...admins];
    if (orgData.adminId && !allAdmins.includes(orgData.adminId)) {
      allAdmins.push(orgData.adminId);
    }

    // Check if user is an admin
    const isAdmin = allAdmins.includes(userId);

    if (isAdmin) {
      // Check if this is the last admin
      if (allAdmins.length === 1) {
        throw new Error(
          "Cannot remove the last admin. Please add another admin first."
        );
      }
    }

    // Remove from admins array
    const updatedAdmins = admins.filter((id) => id !== userId);

    // Also update adminId if it was the main admin
    let updatedAdminId = orgData.adminId;
    if (orgData.adminId === userId && updatedAdmins.length > 0) {
      // If removing the main admin, set first remaining admin as main admin
      updatedAdminId = updatedAdmins[0];
    } else if (orgData.adminId === userId && updatedAdmins.length === 0) {
      // This shouldn't happen due to the check above, but handle it just in case
      throw new Error("Cannot remove the last admin.");
    }

    // Update organization to remove user from admins
    await setDoc(
      orgRef,
      {
        ...orgData,
        adminId: updatedAdminId,
        admins: updatedAdmins,
      },
      { merge: true }
    );

    // Delete all orgMemberRequests for this user/domain
    const memberRequestsRef = collection(db, "orgMemberRequests");
    const memberRequestsQuery = query(
      memberRequestsRef,
      where("userId", "==", userId),
      where("domain", "==", domain)
    );
    const memberRequestsSnapshot = await getDocs(memberRequestsQuery);

    const deleteRequestPromises = memberRequestsSnapshot.docs.map(
      (requestDoc) => deleteDoc(doc(db, "orgMemberRequests", requestDoc.id))
    );
    await Promise.all(deleteRequestPromises);

    // Delete user's Firestore document and all nested collections via Cloud Function
    // This also deletes the Firebase Auth account
    try {
      const deleteUserAccount = httpsCallable(functions, "deleteUserAccount");
      await deleteUserAccount({ userId });
    } catch (authError) {
      console.error("Error deleting user account:", authError);
      // If Cloud Function fails, still try to delete the user document locally
      try {
        const userRef = doc(db, "users", userId);
        await deleteDoc(userRef);
      } catch (docError) {
        console.error("Error deleting user document:", docError);
      }
      throw authError;
    }
  } catch (error) {
    console.error("Error removing org member:", error);
    throw error;
  }
};
